<template>
  <div class="njangi-page">
    <v-container class="njangi-container">
      <!-- Header Section -->
      <div class="njangi-header">
        <div class="njangi-header-icon">
          <v-icon color="white" size="28">mdi-account-group</v-icon>
        </div>
        <div class="njangi-header-text">
          <h1 class="njangi-title">Njangi Participation</h1>
          <p class="njangi-subtitle">Complete the prerequisites below to join</p>
        </div>
      </div>

      <!-- Checklist Card -->
      <v-card class="njangi-card" elevation="0">
        <v-card-text class="njangi-card-body">

          <!-- Progress Indicator -->
          <div class="njangi-progress">
            <div class="njangi-progress-bar">
              <div
                class="njangi-progress-fill"
                :style="{ width: progressPercent + '%' }"
              />
            </div>
            <span class="njangi-progress-text">{{ completedSteps }} of {{ totalSteps }} completed</span>
          </div>

          <!-- Step 1: Identity Verification -->
          <div
            class="njangi-step"
            :class="{
              'njangi-step--completed': kycApproved,
              'njangi-step--pending': !kycApproved
            }"
          >
            <div class="njangi-step-left">
              <div class="njangi-step-badge" :class="kycApproved ? 'badge--success' : 'badge--warning'">
                <v-icon v-if="kycApproved" color="white" size="18">mdi-shield-check</v-icon>
                <span v-else class="badge-num">1</span>
              </div>
              <div class="njangi-step-line" :class="{ 'line--active': kycApproved }" />
            </div>

            <div class="njangi-step-content">
              <div class="njangi-step-header">
                <h3 class="njangi-step-title">Identity Verification</h3>
                <v-chip
                  v-if="kycApproved"
                  x-small
                  color="green"
                  text-color="white"
                  class="njangi-step-chip"
                >
                  <v-icon x-small left>mdi-check</v-icon>
                  Verified
                </v-chip>
                <v-chip
                  v-else
                  x-small
                  color="orange"
                  text-color="white"
                  class="njangi-step-chip"
                >
                  <v-icon x-small left>mdi-clock-outline</v-icon>
                  Required
                </v-chip>
              </div>

              <p class="njangi-step-desc">
                {{ kycApproved
                  ? 'Your identity has been successfully verified.'
                  : 'You must verify your identity before joining any Njangi. This is a mandatory security step.'
                }}
              </p>

              <v-btn
                v-if="!kycApproved"
                color="primary"
                rounded
                depressed
                small
                class="njangi-step-btn"
                @click="goToIdentity"
              >
                <v-icon left small>mdi-shield-account-outline</v-icon>
                Verify My Identity
              </v-btn>
            </div>
          </div>

          <!-- Step 2: Terms of Participation -->
          <div
            class="njangi-step"
            :class="{
              'njangi-step--completed': termsAccepted,
              'njangi-step--pending': !termsAccepted,
              'njangi-step--locked': !kycApproved
            }"
          >
            <div class="njangi-step-left">
              <div
                class="njangi-step-badge"
                :class="termsAccepted ? 'badge--success' : (!kycApproved ? 'badge--disabled' : 'badge--primary')"
              >
                <v-icon v-if="termsAccepted" color="white" size="18">mdi-check</v-icon>
                <span v-else class="badge-num">2</span>
              </div>
            </div>

            <div class="njangi-step-content">
              <div class="njangi-step-header">
                <h3 class="njangi-step-title">Terms of Participation</h3>
                <v-chip
                  v-if="termsAccepted"
                  x-small
                  color="green"
                  text-color="white"
                  class="njangi-step-chip"
                >
                  <v-icon x-small left>mdi-check</v-icon>
                  Accepted
                </v-chip>
              </div>

              <p class="njangi-step-desc">
                Read and accept the terms to confirm your understanding of the obligations and risks involved in Njangi participation.
              </p>

              <!-- Terms Card -->
              <div class="njangi-terms-box">
                <div class="njangi-terms-header">
                  <v-icon small color="primary">mdi-file-document-outline</v-icon>
                  <span>Legal Notice</span>
                </div>
                <p class="njangi-terms-text">{{ legalText }}</p>
              </div>

              <!-- Checkbox -->
              <div
                class="njangi-checkbox-wrap"
                :class="{ 'njangi-checkbox-wrap--disabled': !kycApproved }"
                @click="kycApproved && (termsAccepted = !termsAccepted)"
              >
                <div class="njangi-checkbox" :class="{ 'njangi-checkbox--checked': termsAccepted }">
                  <v-icon v-if="termsAccepted" color="white" size="16">mdi-check</v-icon>
                </div>
                <span class="njangi-checkbox-label">
                  <strong>I have read and accept the terms</strong>
                </span>
                <v-tooltip bottom max-width="300">
                  <template #activator="{ on, attrs }">
                    <v-icon
                      small
                      color="grey lighten-1"
                      class="ml-1"
                      v-bind="attrs"
                      v-on="on"
                    >mdi-information-outline</v-icon>
                  </template>
                  <span>{{ legalText }}</span>
                </v-tooltip>
              </div>
            </div>
          </div>

          <!-- Action Footer -->
          <div class="njangi-footer">
            <div v-if="!kycApproved" class="njangi-footer-hint">
              <v-icon x-small color="orange darken-2" class="mr-1">mdi-lock-outline</v-icon>
              Complete identity verification to unlock
            </div>
            <v-btn
              block
              x-large
              rounded
              depressed
              color="primary"
              class="njangi-confirm-btn"
              :disabled="!canConfirm"
              :loading="confirmLoading"
              @click="confirmTerms"
            >
              <v-icon left>mdi-check-circle-outline</v-icon>
              {{ termsAccepted ? 'Confirm My Commitment' : 'Accept Terms to Continue' }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'NjangiChecklist',

  data() {
    return {
      kycApproved: false,
      termsAccepted: false,
      confirmLoading: false,
      legalText: (
        "By participating in a Njangi (tontine), I acknowledge that I am jointly and severally " +
        "liable for the repayment of any funds entrusted to me by other participants. " +
        "In the event of default, I understand that I may be subject to civil and/or criminal " +
        "prosecution under the laws of my country of residence. The platform reserves the right " +
        "to share my personal information with the competent authorities in the event of a dispute."
      ),
    }
  },

  computed: {
    totalSteps() {
      return 2
    },
    completedSteps() {
      let count = 0
      if (this.kycApproved) count++
      if (this.termsAccepted) count++
      return count
    },
    progressPercent() {
      return (this.completedSteps / this.totalSteps) * 100
    },
    canConfirm() {
      return this.kycApproved && this.termsAccepted
    },
  },

  mounted() {
    this.checkKYCStatus()
  },

  methods: {
  async checkKYCStatus() {
    try {
      const res = await axios.get('/api/v1/home/kyc/status/')
      this.kycApproved = res.data.kyc_status === 'approved'
      // ✅ Initialiser l'état des termes depuis le backend
      this.termsAccepted = res.data.njangi_terms_accepted === true
    } catch (e) {
      this.kycApproved = false
      this.termsAccepted = false
    }
  },
    goToIdentity() {
      this.$router.push('/user_profile/identity')
    },
      // Njangi
      async confirmTerms() {
        if (!this.canConfirm) return
        this.confirmLoading = true
        try {
          await axios.post('/api/v1/home/njangi/accept-terms/', {
            terms_accepted: true,
          })
          this.termsAccepted = true
          this._snack('Your repayment commitment has been recorded.', 'green darken-2')

          // ✅ Redirect to home after 1.5s
          setTimeout(() => this.$router.push('/All'), 1500)

          } catch (e) {
            this._snack('Network error. Please try again.', 'red darken-4')
          } finally {
          this.confirmLoading = false
        }
      },
    _snack(message, color) {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', color)
      this.$store.commit('activateSnackBar', true)
    },
  },
}
</script>

<style scoped>
/* ── Base ── */
.njangi-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f0f4f8 0%, #e8eef5 100%);
  padding: 16px 0 40px;
}

.njangi-container {
  max-width: 640px;
  margin: 0 auto;
}

/* ── Header ── */
.njangi-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
  padding: 0 4px;
}

.njangi-header-icon {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(25, 118, 210, 0.3);
  flex-shrink: 0;
}

.njangi-header-text { min-width: 0; }

.njangi-title {
  font-size: 1.4rem;
  font-weight: 800;
  color: #0d2b5e;
  margin: 0;
  letter-spacing: -0.3px;
}

.njangi-subtitle {
  font-size: 0.88rem;
  color: #78909c;
  margin: 4px 0 0;
}

/* ── Card ── */
.njangi-card {
  border-radius: 24px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06) !important;
  overflow: hidden;
}

.njangi-card-body {
  padding: 28px !important;
}

/* ── Progress ── */
.njangi-progress {
  margin-bottom: 28px;
}

.njangi-progress-bar {
  width: 100%;
  height: 6px;
  background: #e3eaf3;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
}

.njangi-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #1976d2, #42a5f5);
  border-radius: 3px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.njangi-progress-text {
  font-size: 0.78rem;
  font-weight: 600;
  color: #78909c;
}

/* ── Steps ── */
.njangi-step {
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
  position: relative;
}

.njangi-step:last-of-type {
  margin-bottom: 0;
}

.njangi-step--locked {
  opacity: 0.5;
  pointer-events: none;
}

/* Step Left (badge + line) */
.njangi-step-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}

.njangi-step-badge {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  transition: all 0.3s ease;
  z-index: 1;
}

.badge--success {
  background: linear-gradient(135deg, #388e3c, #66bb6a);
  box-shadow: 0 2px 8px rgba(56, 142, 60, 0.3);
}

.badge--warning {
  background: linear-gradient(135deg, #f57c00, #ffb74d);
  box-shadow: 0 2px 8px rgba(245, 124, 0, 0.3);
}

.badge--primary {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
}

.badge--disabled {
  background: #e3eaf3;
  color: #90a4ae;
}

.badge-num {
  color: #fff;
  font-weight: 700;
}

.njangi-step-line {
  width: 2px;
  flex: 1;
  min-height: 40px;
  background: #e3eaf3;
  margin: 4px 0;
  transition: background 0.3s ease;
}

.line--active {
  background: linear-gradient(180deg, #388e3c, #e3eaf3);
}

/* Step Content */
.njangi-step-content {
  flex: 1;
  padding-bottom: 24px;
  min-width: 0;
}

.njangi-step-header {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.njangi-step-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1a237e;
  margin: 0;
}

.njangi-step-chip {
  font-weight: 700 !important;
  letter-spacing: 0.3px !important;
}

.njangi-step-desc {
  font-size: 0.85rem;
  color: #78909c;
  line-height: 1.6;
  margin: 0 0 12px;
}

.njangi-step-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
}

/* ── Terms Box ── */
.njangi-terms-box {
  background: linear-gradient(135deg, #f7faff 0%, #fff 100%);
  border: 1px solid rgba(25, 118, 210, 0.1);
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 16px;
}

.njangi-terms-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1976d2;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.njangi-terms-text {
  font-size: 0.8rem;
  color: #546e7a;
  line-height: 1.6;
  margin: 0;
}

/* ── Custom Checkbox ── */
.njangi-checkbox-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1.5px solid #e3eaf3;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.njangi-checkbox-wrap:hover {
  border-color: rgba(25, 118, 210, 0.3);
  background: rgba(25, 118, 210, 0.02);
}

.njangi-checkbox-wrap--disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.njangi-checkbox {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid #b0bec5;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.njangi-checkbox--checked {
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  border-color: #1976d2;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
}

.njangi-checkbox-label {
  font-size: 0.9rem;
  color: #37474f;
}

/* ── Footer ── */
.njangi-footer {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.njangi-footer-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #f57c00;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: rgba(245, 124, 0, 0.06);
  border-radius: 10px;
}

.njangi-confirm-btn {
  text-transform: none !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px !important;
  font-size: 1rem !important;
  height: 52px !important;
  transition: all 0.2s ease;
}

.njangi-confirm-btn:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(25, 118, 210, 0.25);
}

/* ── Responsive ── */
@media (max-width: 600px) {
  .njangi-page {
    padding: 12px 0 30px;
  }

  .njangi-container {
    padding: 0 12px;
  }

  .njangi-header {
    margin-bottom: 16px;
  }

  .njangi-header-icon {
    width: 44px;
    height: 44px;
    border-radius: 14px;
  }

  .njangi-title {
    font-size: 1.2rem;
  }

  .njangi-subtitle {
    font-size: 0.82rem;
  }

  .njangi-card-body {
    padding: 20px 16px !important;
  }

  .njangi-step {
    gap: 12px;
  }

  .njangi-step-badge {
    width: 32px;
    height: 32px;
  }

  .njangi-step-title {
    font-size: 0.95rem;
  }

  .njangi-step-desc {
    font-size: 0.82rem;
  }

  .njangi-terms-box {
    padding: 12px;
  }

  .njangi-terms-text {
    font-size: 0.78rem;
  }

  .njangi-confirm-btn {
    font-size: 0.95rem !important;
    height: 48px !important;
  }
}
</style>